import { useCallback, useEffect, useRef, useState } from 'react'
import { ReactComponent as ChevronDownIcon } from '../../../../images/icons/chevron-down.svg'
import { ReactComponent as DeleteIcon } from '../../../../images/icons/delete.svg'
import { ReactComponent as InfoIcon } from '../../../../images/icons/info.svg'
import { useToggle } from '../../../../hooks'
import { formatAddress } from '../../../../lib/utils'
import { LAB_ORDER_ATTRIBUTES } from '../../../../lib/const'
import AddAddress from './AddAddress'
import { useCheckout } from '../../useCheckout'

const LOCAL_PICKUP = 'localPickup'
const NEW_ADDRESS = 'newAddress'

const Address = () => {
  const { updateOrder, deleteAddress, profile, order, selectedShippingDestination, setSelectedShippingDestination } = useCheckout()
  const [showNewAddress, toggleShowNewAddress, setShowNewAddress] = useToggle(false)
  const [isOpen, setIsOpen] = useState(false)
  const [hoveredAddress, setHoveredAddress] = useState(null)
  const dropdownRef = useRef(null)

  const addresses = profile?.addresses?.data

  const orderWithAddress = selectedShippingDestination === LOCAL_PICKUP
    ? null
    : selectedShippingDestination
  const orderWithLocalPickup = selectedShippingDestination === LOCAL_PICKUP ? LOCAL_PICKUP : null
  const selectedAddress = addresses?.find(({ attributes }) => attributes.guid === orderWithAddress)
  const selectedLabel = selectedAddress
    ? formatAddress(selectedAddress.attributes)
    : orderWithLocalPickup
      ? 'Local pickup in store'
      : 'Select an address'

  useEffect(() => {
    const closeDropdown = (event) => {
      if (!dropdownRef.current?.contains(event.target)) setIsOpen(false)
    }
    document.addEventListener('mousedown', closeDropdown)
    return () => document.removeEventListener('mousedown', closeDropdown)
  }, [])

  const handleChange = useCallback(async (newAddressId) => {
    setIsOpen(false)
    if (newAddressId === NEW_ADDRESS) {
      toggleShowNewAddress()
      return
    }
    setShowNewAddress(false)
    setSelectedShippingDestination(newAddressId)
    if (newAddressId === LOCAL_PICKUP) {
      updateOrder({
        type: LAB_ORDER_ATTRIBUTES.localPickup,
        value: true,
      })
    } else {
      // updateOrder({
      //   type: LAB_ORDER_ATTRIBUTES.localPickup,
      //   value: false,
      // })
      updateOrder({
        type: LAB_ORDER_ATTRIBUTES.shippingAddressId,
        value: newAddressId,
      })
    }
  }, [])

  return (
    <>
      <div className="w-layout-grid grid-form _1fr">
        <div
          id="w-node-_83a8a9d7-764d-a757-9321-a96ba32c3ac9-80e80a52"
          className="input-container paper-type"
        >
          <div className="label-container">
            <div className="label-subcontainer">
              <div className="sub-label-form">Ship to</div>
              <div className="icon mini margin-left opacity70 w-embed">
                <InfoIcon />
              </div>
            </div>
          </div>
          <div className="form-block-input w-form">
            <div
              id="email-form"
              name="email-form"
              data-name="Email Form"
              className="form-input select-field"
              style={{ position: 'relative' }}
            >
              <div ref={dropdownRef} className="select-field-wrapper" style={{ position: 'relative' }}>
                <button
                  type="button"
                  id="address"
                  name="address"
                  className="form-select-field font-size-14 w-select"
                  aria-haspopup="listbox"
                  aria-expanded={isOpen}
                  onClick={() => setIsOpen((open) => !open)}
                  style={{
                    width: '100%',
                    paddingRight: '30px',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  {selectedLabel}
                </button>
                  <ChevronDownIcon className="img-icon"
                     style={{
                      position: 'absolute',
                      right: '15px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                    }} />
                {isOpen && (
                  <div
                    role="listbox"
                    aria-label="Addresses"
                    style={{
                      position: 'absolute',
                      zIndex: 10,
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: '#fff',
                      border: '1px solid #767676',
                    }}
                  >
                    {addresses?.map(({ id, attributes }) => (
                      <div
                        key={attributes.guid}
                        onMouseEnter={() => setHoveredAddress(id)}
                        onMouseLeave={() => setHoveredAddress(null)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          background: attributes.guid === orderWithAddress ? '#1967d2' : '#fff',
                          color: attributes.guid === orderWithAddress ? '#fff' : '#222',
                        }}
                      >
                        <button
                          type="button"
                          role="option"
                          aria-selected={attributes.guid === orderWithAddress}
                          onClick={() => handleChange(attributes.guid)}
                          style={{
                            flex: 1,
                            minHeight: '25px',
                            border: 0,
                            padding: '2px 8px',
                            background: 'transparent',
                            color: 'inherit',
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          {formatAddress(attributes)}
                        </button>
                        {hoveredAddress === id && (
                          <button
                            type="button"
                            aria-label="Delete address"
                            onClick={(event) => {
                              event.stopPropagation()
                              deleteAddress(id)
                            }}
                            style={{
                              width: '26px',
                              height: '25px',
                              border: 0,
                              padding: '4px',
                              background: 'transparent',
                              color: 'inherit',
                              cursor: 'pointer',
                            }}
                          >
                            <DeleteIcon />
                          </button>
                        )}
                      </div>
                    ))}
                    {[['New Address', NEW_ADDRESS], ['Local pickup in store', LOCAL_PICKUP]].map(
                      ([label, value]) => (
                        <button
                          key={value}
                          type="button"
                          role="option"
                          aria-selected={value === orderWithLocalPickup}
                          onClick={() => handleChange(value)}
                          style={{
                            display: 'block',
                            width: '100%',
                            minHeight: '25px',
                            border: 0,
                            padding: '2px 8px',
                            background: value === orderWithLocalPickup ? '#1967d2' : '#fff',
                            color: value === orderWithLocalPickup ? '#fff' : '#222',
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          {label}
                        </button>
                      ),
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showNewAddress && <AddAddress onSave={toggleShowNewAddress} />}
    </>
  )
}

export default Address
